// Configuration Constants
const ETX_CONFIG = {
  VIEWPORT_OFFSET: -10,
  ANIMATION_REPEAT: false,
  ANIMATION_STAGGER: 150,
  COUNT_DURATION: 1750,
};

// ---------------------------------------------------------------------------
// SVG-Pfade: zuerst alles messen (READ), danach schreiben (WRITE)
// → höchstens ein Layout pro Aufruf statt eines pro Pfad
// ---------------------------------------------------------------------------
const measurePaths = (wrappers) => {
  const svgRects = new Map();
  const result = [];

  wrappers.forEach((wrapper) => {
    wrapper.querySelectorAll("svg path").forEach((path) => {
      const userLen = path.dataset.pathLengthUser
        ? Number(path.dataset.pathLengthUser)
        : path.getTotalLength();

      let len = userLen;
      const svg = path.ownerSVGElement;
      const vb = svg?.viewBox?.baseVal;

      if (svg && vb && vb.width && vb.height) {
        let rect = svgRects.get(svg);
        if (!rect) {
          rect = svg.getBoundingClientRect();
          svgRects.set(svg, rect);
        }
        if (rect.width && rect.height) {
          len = userLen * ((rect.width / vb.width + rect.height / vb.height) / 2);
        }
      }

      result.push({ path, userLen, len });
    });
  });

  return result;
};

const applyPathLength = ({ path, userLen, len }, drawn) => {
  if (!path.dataset.pathLengthUser) path.dataset.pathLengthUser = String(userLen);
  path.style.strokeDasharray = String(len);
  path.style.strokeDashoffset = drawn ? "0" : String(len);
  path.style.setProperty("--stripe-start", String(len));
};

const setPathsHidden = (wrappers) => {
  measurePaths(wrappers).forEach((m) => {
    m.path.style.animation = "none";
    m.path.classList.remove("is-drawn");
    applyPathLength(m, false);
  });
};

const drawPaths = (wrappers) => {
  const measured = measurePaths(wrappers);

  measured.forEach((m) => {
    m.path.style.animation = "none";
    applyPathLength(m, false);
  });

  // Animation zwei Frames später starten statt per getBoundingClientRect() neu zu triggern
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      measured.forEach(({ path }) => {
        path.style.animation = "headline-stripe var(--transition-duration) ease-out forwards";
        path.classList.add("is-drawn");
      });
    });
  });
};

const animateCountUp = (el, countTo) => {
  const start = parseInt(el.textContent, 10) || 0;
  const startTime = performance.now();

  const animate = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / ETX_CONFIG.COUNT_DURATION, 1);
    el.textContent = Math.floor(start + (countTo - start) * progress);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      el.textContent = countTo;
    }
  };

  requestAnimationFrame(animate);
};

// ---------------------------------------------------------------------------
// Observer initialisation
// ---------------------------------------------------------------------------
function etxInitObservers() {
  const isShopifyDesignMode = document.documentElement.classList.contains("shopify-design-mode");

  const bottomMargin = Math.abs(ETX_CONFIG.VIEWPORT_OFFSET);
  const inViewOptions = { rootMargin: `0px 0px ${bottomMargin}px 0px`, threshold: 0 };
  const exitOptions = { threshold: 0 };

  const drawnPaths = new Set();
  window.etxDrawnPaths = drawnPaths;

  // Liegt das Element bereits oberhalb des Viewports? (Position kommt gratis vom Observer)
  const isAbove = (entry) => !entry.isIntersecting && entry.boundingClientRect.bottom < 0;

  // --- helpers ---

  const triggerAppear = (el) => {
    el.classList.remove("will-appear");
    el.classList.add("did-appear");
    if (ETX_CONFIG.ANIMATION_REPEAT && !isShopifyDesignMode) {
      appearResetObserver.observe(el);
    }
  };

  const markDrawn = (wrapper) => {
    wrapper.querySelectorAll("svg path").forEach((path) => {
      path.style.strokeDashoffset = "0";
      path.classList.add("is-drawn");
    });
    wrapper.classList.remove("will-drawPath");
    wrapper.classList.add("did-drawPath");
    drawnPaths.add(wrapper);
    if (ETX_CONFIG.ANIMATION_REPEAT && !isShopifyDesignMode) {
      drawResetObserver.observe(wrapper);
    }
  };

  const triggerDraw = (wrapper) => {
    if (!wrapper.isConnected || !wrapper.querySelector("svg path")) return;
    drawPaths([wrapper]);
    wrapper.classList.remove("will-drawPath");
    wrapper.classList.add("did-drawPath");
    drawnPaths.add(wrapper);
    if (ETX_CONFIG.ANIMATION_REPEAT && !isShopifyDesignMode) {
      drawResetObserver.observe(wrapper);
    }
  };

  const showCount = (el) => {
    const countTo = parseInt(el.getAttribute("data-count"), 10);
    if (Number.isNaN(countTo)) return;
    el.textContent = countTo;
    el.classList.remove("will-countUp");
    el.classList.add("did-countUp");
  };

  // Stagger: Einträge von oben nach unten sortieren und gestaffelt auslösen
  const staggerBatch = (entering, callback) => {
    entering
      .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
      .forEach(({ target }, i) => {
        const delay = i * ETX_CONFIG.ANIMATION_STAGGER;
        if (delay === 0) {
          callback(target);
        } else {
          setTimeout(() => target.isConnected && callback(target), delay);
        }
      });
  };

  // --- APPEAR ---

  const appearObserver = new IntersectionObserver((entries) => {
    if (isShopifyDesignMode) return;
    const entering = [];

    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        appearObserver.unobserve(entry.target);
        entering.push(entry);
      } else if (isAbove(entry)) {
        // Bereits vorbeigescrollt (z. B. Reload mitten auf der Seite) → sofort zeigen
        appearObserver.unobserve(entry.target);
        triggerAppear(entry.target);
      }
    });

    staggerBatch(entering, triggerAppear);
  }, inViewOptions);

  const appearResetObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) return;
      const rootBottom = entry.rootBounds?.bottom ?? window.innerHeight;
      if (entry.boundingClientRect.top > rootBottom) {
        const el = entry.target;
        appearResetObserver.unobserve(el);
        el.classList.remove("did-appear");
        el.classList.add("will-appear");
        appearObserver.observe(el);
      }
    });
  }, exitOptions);

  // --- DRAW PATH ---

  const drawObserver = new IntersectionObserver((entries) => {
    if (isShopifyDesignMode) return;
    const entering = [];

    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        drawObserver.unobserve(entry.target);
        entering.push(entry);
      } else if (isAbove(entry)) {
        drawObserver.unobserve(entry.target);
        markDrawn(entry.target);
      }
    });

    staggerBatch(entering, triggerDraw);
  }, inViewOptions);

  const drawResetObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) return;
      const rootBottom = entry.rootBounds?.bottom ?? window.innerHeight;
      if (entry.boundingClientRect.top > rootBottom) {
        const wrapper = entry.target;
        drawResetObserver.unobserve(wrapper);
        drawnPaths.delete(wrapper);
        wrapper.classList.remove("did-drawPath");
        wrapper.classList.add("will-drawPath");
        setPathsHidden([wrapper]);
        drawObserver.observe(wrapper);
      }
    });
  }, exitOptions);

  // --- COUNT UP ---

  const countUpObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const el = entry.target;

      if (isAbove(entry)) {
        countUpObserver.unobserve(el);
        showCount(el);
        return;
      }
      if (!entry.isIntersecting) return;

      const countTo = parseInt(el.getAttribute("data-count"), 10);
      if (Number.isNaN(countTo)) return;
      countUpObserver.unobserve(el);
      el.classList.remove("will-countUp");
      el.classList.add("did-countUp");
      animateCountUp(el, countTo);
      if (ETX_CONFIG.ANIMATION_REPEAT) {
        countUpResetObserver.observe(el);
      }
    });
  }, inViewOptions);

  const countUpResetObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) return;
      const rootBottom = entry.rootBounds?.bottom ?? window.innerHeight;
      if (entry.boundingClientRect.top > rootBottom) {
        const el = entry.target;
        countUpResetObserver.unobserve(el);
        el.textContent = "0";
        el.classList.remove("did-countUp");
        el.classList.add("will-countUp");
        countUpObserver.observe(el);
      }
    });
  }, exitOptions);

  window.etxObservers = {
    appear: appearObserver,
    appearReset: appearResetObserver,
    draw: drawObserver,
    drawReset: drawResetObserver,
    countUp: countUpObserver,
    countUpReset: countUpResetObserver,
  };
}

// Neue Elemente registrieren – ohne getBoundingClientRect pro Element.
// Ob ein Element schon oberhalb liegt, meldet der Observer im ersten Callback.
function etxScanNewElements() {
  const observers = window.etxObservers;
  if (!observers) return;

  document.querySelectorAll(".will-appear").forEach((el) => {
    observers.appear.observe(el);
  });

  const wrappers = Array.from(document.querySelectorAll(".will-drawPath"));
  const fresh = wrappers.filter((w) => !w.dataset.pathsInitialized);
  if (fresh.length) {
    setPathsHidden(fresh); // eine Messrunde für alle, dann schreiben
    fresh.forEach((w) => { w.dataset.pathsInitialized = "true"; });
  }
  wrappers.forEach((w) => observers.draw.observe(w));

  document.querySelectorAll(".will-countUp").forEach((el) => {
    if (Number.isNaN(parseInt(el.getAttribute("data-count"), 10))) return;
    observers.countUp.observe(el);
  });
}

// Main entry point — safe to call multiple times (e.g. after section re-renders).
function etxInViewport() {
  if (!window.etxObserversInitialized) {
    window.etxObserversInitialized = true;
    etxInitObservers();
  }
  etxScanNewElements();
}

// Pfadlängen neu berechnen (wird von etx_ui.js bei Breitenänderung aufgerufen)
function etxRecalcPathLengths() {
  const drawnPaths = window.etxDrawnPaths;
  if (!drawnPaths) return;

  const wrappers = [];
  drawnPaths.forEach((wrapper) => {
    if (wrapper.isConnected) wrappers.push(wrapper);
    else drawnPaths.delete(wrapper);
  });

  measurePaths(wrappers).forEach((m) => {
    applyPathLength(m, m.path.classList.contains("is-drawn"));
  });
}

// Initialize
// (kein eigener resize-Listener mehr – etx_ui.js ruft etxRecalcPathLengths
//  gedrosselt und nur bei Breitenänderung auf)
if (typeof window !== "undefined") {
  window.addEventListener("load", etxInViewport);
}

// Cleanup function (for SPA / theme section teardown)
function etxCleanup() {
  const observers = window.etxObservers;
  if (observers) {
    Object.values(observers).forEach((obs) => obs.disconnect());
    window.etxObservers = null;
  }
  window.etxObserversInitialized = false;
  window.etxDrawnPaths = null;
}

// Export for external use if needed
window.etxInViewport = etxInViewport;
window.etxRecalcPathLengths = etxRecalcPathLengths;
window.etxCleanup = etxCleanup;